/**
 * Admin → Classifier Vocab
 *
 * Phase 6 - tenant-supplied classifier overlays (custom column-name
 * patterns / regex / keywords) layered on top of an industry pack's
 * built-in vocabulary. Overlays only apply when the pack they target
 * is active for the asset's BU at scan time.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Plus, Trash2, Loader2, AlertTriangle, CheckCircle2, Hash } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  listIndustryPacks,
  listClassifierVocab,
  createClassifierVocab,
  deleteClassifierVocab,
  type ClassifierOverlay,
  type ClassifierOverlayPatternKind,
  type IndustryPackAnchor,
  type IndustryPackSummary,
} from '@/lib/api';

interface CreateInput {
  patternKind: ClassifierOverlayPatternKind;
  pattern: string;
  label: string;
  confidenceBoost: number;
  regulations: string;
  notes: string;
}

const DEFAULT_INPUT: CreateInput = {
  patternKind: 'column_name',
  pattern: '',
  label: '',
  confidenceBoost: 0.75,
  regulations: '',
  notes: '',
};

export function AdminClassifierVocab(): JSX.Element {
  const [packs, setPacks] = useState<IndustryPackSummary[]>([]);
  const [selectedPack, setSelectedPack] = useState<IndustryPackAnchor | null>(null);
  const [overlays, setOverlays] = useState<ClassifierOverlay[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createInput, setCreateInput] = useState<CreateInput>(DEFAULT_INPUT);

  const refreshPacks = useCallback(async () => {
    try {
      const result = await listIndustryPacks();
      setPacks(result.packs);
      // Default to the first active pack
      const firstActive = result.packs.find((p) => p.tenantActivation.activated);
      if (firstActive && !selectedPack) setSelectedPack(firstActive.anchor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load packs');
    }
  }, [selectedPack]);

  const refreshOverlays = useCallback(async () => {
    if (!selectedPack) {
      setOverlays([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await listClassifierVocab(selectedPack);
      setOverlays(result.overlays);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load overlays');
    } finally {
      setLoading(false);
    }
  }, [selectedPack]);

  useEffect(() => {
    void refreshPacks();
  }, [refreshPacks]);

  useEffect(() => {
    void refreshOverlays();
  }, [refreshOverlays]);

  async function handleCreate(): Promise<void> {
    if (!selectedPack || !createInput.pattern.trim() || !createInput.label.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const regs = createInput.regulations
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean);
      await createClassifierVocab(selectedPack, {
        patternKind: createInput.patternKind,
        pattern: createInput.pattern.trim(),
        label: createInput.label.trim(),
        confidenceBoost: createInput.confidenceBoost,
        regulations: regs.length > 0 ? regs : undefined,
        notes: createInput.notes.trim() || undefined,
      });
      setCreateOpen(false);
      setCreateInput(DEFAULT_INPUT);
      await refreshOverlays();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to create overlay');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(overlay: ClassifierOverlay): Promise<void> {
    if (!window.confirm(`Delete classifier overlay "${overlay.pattern}" → ${overlay.label}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteClassifierVocab(overlay.packId, overlay.id);
      await refreshOverlays();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to delete overlay');
    } finally {
      setBusy(false);
    }
  }

  const activePacks = packs.filter((p) => p.tenantActivation.activated);
  const currentPack = packs.find((p) => p.anchor === selectedPack);

  return (
    <PageContainer>
      <PageHeader
        title="Admin · Classifier Vocab"
        subtitle="Tenant-supplied classifier overlays on top of industry pack vocabularies. Overlays only apply when their target pack is active for the asset's BU at scan time. Useful for org-specific column-name conventions and custom regex."
        badges={[
          { label: `${activePacks.length} active packs`, variant: 'success' },
          { label: `${overlays.length} overlays for current pack`, variant: 'glass' },
        ]}
      />

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">{error}</p>
        </GlassCard>
      )}

      <GlassCard className="p-5 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">Pack:</label>
            <select
              value={selectedPack ?? ''}
              onChange={(e) => setSelectedPack(e.target.value as IndustryPackAnchor)}
              className="glass-lite rounded-md px-2 py-1 text-xs outline-none min-w-[220px]"
            >
              {packs.length === 0 && <option value="">No packs available</option>}
              {packs.map((p) => (
                <option key={p.anchor} value={p.anchor}>
                  {p.name} {p.tenantActivation.activated ? '· active' : '· (inactive)'}
                </option>
              ))}
            </select>
            {currentPack && !currentPack.tenantActivation.activated && (
              <Badge variant="warning" className="text-[10px] gap-1">
                <AlertTriangle className="h-3 w-3" />
                Pack inactive - overlays won't apply
              </Badge>
            )}
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            size="sm"
            disabled={!selectedPack || busy}
          >
            <Plus className="h-3 w-3" />
            New overlay
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> loading overlays…
          </div>
        ) : overlays.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Hash className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No tenant overlays for this pack yet.</p>
            <p className="text-[11px] mt-1">
              Add your org-specific column-name patterns or regex to refine classification.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {overlays.map((o, i) => (
              <motion.div
                key={o.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.02, 0.3) }}
                className="glass-lite rounded-md p-3 flex items-center gap-3"
              >
                <Badge
                  variant={
                    o.patternKind === 'regex'
                      ? 'info'
                      : o.patternKind === 'keyword'
                        ? 'glass'
                        : 'success'
                  }
                  className="text-[10px] shrink-0"
                >
                  {o.patternKind}
                </Badge>
                <code className="font-mono text-xs truncate flex-1 min-w-0">{o.pattern}</code>
                <span className="text-[10px] text-muted-foreground shrink-0">
                  → <code className="font-mono">{o.label}</code>
                </span>
                <span className="text-[10px] text-muted-foreground shrink-0">
                  conf {o.confidenceBoost.toFixed(2)}
                </span>
                {o.regulations.length > 0 && (
                  <div className="flex flex-wrap gap-1 shrink-0">
                    {o.regulations.slice(0, 3).map((r) => (
                      <Badge key={r} variant="glass" className="text-[9px]">
                        {r}
                      </Badge>
                    ))}
                  </div>
                )}
                <Button
                  onClick={() => handleDelete(o)}
                  variant="glass"
                  size="sm"
                  disabled={busy}
                  className="shrink-0"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </GlassCard>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New classifier overlay</DialogTitle>
            <DialogDescription>
              Adds a tenant-specific pattern to {currentPack?.name ?? 'this pack'}. Applies only
              when the pack is active for the asset's BU at scan time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
                Pattern kind
              </label>
              <select
                value={createInput.patternKind}
                onChange={(e) =>
                  setCreateInput({
                    ...createInput,
                    patternKind: e.target.value as ClassifierOverlayPatternKind,
                  })
                }
                className="w-full glass-lite rounded-md p-2 text-xs outline-none"
              >
                <option value="column_name">column_name</option>
                <option value="keyword">keyword</option>
                <option value="regex">regex</option>
              </select>
            </div>
            <FormField
              label="Pattern"
              value={createInput.pattern}
              onChange={(v) => setCreateInput({ ...createInput, pattern: v })}
              placeholder={createInput.patternKind === 'regex' ? '^cust_aadhaar_\\d+$' : 'cust_aadhaar_alt'}
              mono
              required
            />
            <FormField
              label="Classification label"
              value={createInput.label}
              onChange={(v) => setCreateInput({ ...createInput, label: v })}
              placeholder="aadhaar / pan / phone_in / ..."
              mono
              required
            />
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
                Confidence boost (0.00 - 1.00)
              </label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={createInput.confidenceBoost}
                onChange={(e) =>
                  setCreateInput({ ...createInput, confidenceBoost: Number(e.target.value) })
                }
                className="w-full glass-lite rounded-md p-2 text-xs outline-none"
              />
            </div>
            <FormField
              label="Regulations (comma-separated, optional)"
              value={createInput.regulations}
              onChange={(v) => setCreateInput({ ...createInput, regulations: v })}
              placeholder="DPDPA, RBI_MD_Cyber"
            />
            <FormField
              label="Notes (optional)"
              value={createInput.notes}
              onChange={(v) => setCreateInput({ ...createInput, notes: v })}
              placeholder="Why this overlay was added"
              textarea
            />
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleCreate}
              disabled={busy || !createInput.pattern.trim() || !createInput.label.trim()}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              Create
            </Button>
            <Button onClick={() => setCreateOpen(false)} variant="glass" disabled={busy}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function FormField({
  label,
  value,
  onChange,
  placeholder,
  required,
  mono,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  mono?: boolean;
  textarea?: boolean;
}): JSX.Element {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          className={
            'w-full glass-lite rounded-md p-2 text-xs outline-none resize-none ' +
            (mono ? 'font-mono' : '')
          }
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={
            'w-full glass-lite rounded-md p-2 text-xs outline-none ' + (mono ? 'font-mono' : '')
          }
        />
      )}
    </div>
  );
}
